<template>
  <div class="w-full min-h-[80vh] flex flex-col">
    <div class="hierarchy-tree w-full flex-1 relative overflow-hidden border border-gray-300">
      <div class="absolute top-4 right-[160px] z-10" v-if="userData.length">
        <div class="relative inline-block">
          <v-select
            v-model="currentDirection"
            :items="directions"
            item-text="label"
            item-value="value"
            @change="changeDirection"
            outlined
            dense
            class="text-xs font-medium text-gray-700"
          >
          </v-select>
        </div>
      </div>

      <div id="svg-tree" class="w-full h-full absolute" v-if="userData.length"></div>
      <NoData
        v-else
        title="Nothing to display"
        subTitle="Please create the hierarchy-tree for your organization"
        class="w-full h-full flex items-center justify-center absolute inset-0"
      />
    </div>

    <!-- Hidden UserAvatar component for reference (not rendered) -->
    <div class="hidden">
      <UserAvatar ref="userAvatar" :user="sampleUser" />
    </div>
  </div>
</template>

<script>
import NoData from '@/components/NoData.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import { axiosUrl } from '@/utils/workforceApi'
import ApexTree from 'apextree'

export default {
  name: 'OrgHierarchyTree',
  components: {
    NoData,
    UserAvatar
  },
  data() {
    return {
      userData: [],
      baseUrl: process.env.VUE_APP_S3_BASE_URL,
      currentDirection: 'top',
      isDropdownOpen: false,
      tree: null,
      sampleUser: { firstName: '', lastName: '' }, // Sample user for UserAvatar component
      directions: [
        { label: 'Top to Bottom', value: 'top' },
        { label: 'Bottom to Top', value: 'bottom' },
        { label: 'Left to Right', value: 'left' },
        { label: 'Right to Left', value: 'right' }
      ]
    }
  },
  mounted() {
    this.fetchEmployees()
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.dropdown-container')
      if (dropdown && !dropdown.contains(event.target)) {
        this.isDropdownOpen = false
      }
    },

    changeDirection(direction) {
      this.currentDirection = direction
      this.isDropdownOpen = false
      this.renderApexTree()
    },
    async fetchEmployees() {
      try {
        const allUser = {
          limit: '*',
          sortBy: 'firstName',
          sortOrder: 'asc',
          skipHierarchy: true
        }
        const response = await this.$axios.get(axiosUrl.employees.getEmployees, {
          params: allUser
        })
        if (response.data.success) {
          this.userData = this.structureData(response.data.users)
          this.$nextTick(() => {
            this.renderApexTree()
          })
        }
      } catch (error) {
        console.error('Error fetching employees:', error)
      }
    },
    structureData(data) {
      const map = {}

      data.forEach((item) => {
        const id = item._id
        if (!map[id]) {
          map[id] = { ...item, children: [] }
        }
      })

      const result = []
      let ownerNode = null

      // Find the owner node (user with isOwner = true)
      data.forEach((item) => {
        if (item.systemUserId?.isOwner === true) {
          ownerNode = map[item._id]
        }
      })

      // If no owner found, we can't properly structure the data
      if (!ownerNode) {
        console.warn('No owner found in the organization data')
        return []
      }

      data.forEach((item) => {
        const id = item._id
        const reportsToId = item.reportesTo?._id

        if (reportsToId) {
          if (!map[reportsToId]) {
            map[reportsToId] = { id: reportsToId, children: [] }
          }
          // if (reportsToId && map[reportsToId]) {
          map[reportsToId].children.push(map[id])
        } else {
          // If item has no reportsTo or reportsToId is missing,
          // and it's not the owner itself, connect it to the owner
          if (id !== ownerNode._id) {
            ownerNode.children.push(map[id])
          }
        }
      })

      // Add the owner node to result
      result.push(ownerNode)

      return result
    },
    renderApexTree(autoFit = false) {
      if (!this.userData.length) return

      this.$nextTick(() => {
        const popupContainer = document.querySelector('.hierarchy-tree')

        // Get exact container dimensions
        let containerWidth = popupContainer ? popupContainer.offsetWidth : window.innerWidth
        let containerHeight = popupContainer ? popupContainer.offsetHeight : window.innerHeight

        // Ensure minimum dimensions
        containerWidth = Math.max(containerWidth, 800)
        containerHeight = Math.max(containerHeight, 600)

        // For auto-fit, calculate optimal dimensions based on tree size
        if (autoFit) {
          const nodeCount = this.countNodes(this.userData[0])
          const estimatedWidth = Math.max(containerWidth, nodeCount * 200)
          const estimatedHeight = Math.max(containerHeight, 600)
          containerWidth = estimatedWidth
          containerHeight = estimatedHeight
        }

        const apexData = this.convertToApexFormat(this.userData[0])

        const options = {
          contentKey: 'data',
          width: containerWidth,
          height: containerHeight,
          nodeWidth: 75,
          nodeHeight: 45,
          childrenSpacing: 70,
          siblingSpacing: 40,
          direction: this.currentDirection,
          fontSize: '20px',
          fontFamily: 'sans-serif',
          fontWeight: '500',
          fontColor: '#a06dcc',
          borderWidth: 1,
          borderColor: '#a06dcc',
          canvasStyle: 'margin-top:15px background: #f6f6f6; width: 100%; height: 100%;',
          nodeTemplate: (content) => {
            let imageUrl = content.imageURL
            // let roleName = content.role || ''
            let initials = content.initials || ''
            let hasProfileImage = content.hasProfileImage

            // If there's a profile image, show it; otherwise show initials
            let imageHtml = ''
            if (hasProfileImage) {
              imageHtml = `<img style="width: 10px; height: 10px; border-radius: 50%; object-fit: cover;" src="${imageUrl}" alt="" onerror="this.onerror=null;this.src='${this.getFallbackImage(
                content.gender
              )}';">`
            } else {
              // Show initials with a colored background when no profile image
              const bgColor = this.getInitialsBackgroundColor(initials)
              imageHtml = `<div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${bgColor}; display: flex; align-items: center; justify-content: center; font-size: 5px; font-weight: bold; color: white;">${initials}</div>`
            }

            return `<div style="display: flex; flex-direction: row; justify-content:space-evenly; align-items: center; height: 100%; padding: 0 3px; border-radius: 6px;">
                ${imageHtml}
                <div style="display: flex; flex-direction: column; align-items: flex-start; margin-left: 4px; width: 55px;">
                  <div style="font-weight: bold; font-family: Arial; font-size: 5px; line-height: 1.2; word-wrap: break-word; white-space: normal;">${content.name} / ${content.employeeId}</div>
                  <div style="font-family: Arial; font-size: 4px; color: #555; line-height: 1.2; word-wrap: break-word; white-space: normal; margin-top: 2px;">${content.designation}</div>
                </div>
              </div>`
          },
          nodeStyle: 'border-radius: 4px;',
          enableToolbar: true
        }

        // Clear existing tree
        const treeContainer = document.getElementById('svg-tree')
        if (treeContainer) {
          treeContainer.innerHTML = ''
        }

        // Initialize ApexTree
        this.tree = new ApexTree(document.getElementById('svg-tree'), options)
        this.tree.render(apexData)
      })
    },
    countNodes(node) {
      let count = 1
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          count += this.countNodes(child)
        })
      }
      return count
    },
    convertToApexFormat(node) {
      const visited = new Set()

      const convertNode = (userNode) => {
        if (!userNode || visited.has(userNode._id)) {
          return null
        }
        visited.add(userNode._id)

        const role = userNode.wfmRole ? userNode.wfmRole.role : ''
        const bgColor = this.getRoleColor(role)
        const fullName = userNode.fullName || ''
        const initials = this.getUserInitials(fullName)

        const result = {
          id: userNode._id,
          data: {
            name: fullName,
            initials: initials,
            imageURL: this.getEmployeeImage(userNode),
            role: role,
            employeeId: userNode.employeeId || '',
            designation: userNode.designation?.designationName || '',
            gender: userNode.gender || 'O',
            hasProfileImage: !!userNode.systemUserId?.profileImage
          },
          options: {
            nodeBGColor: bgColor
          }
        }

        // Convert children if they exist
        if (userNode.children && userNode.children.length > 0) {
          result.children = userNode.children
            .map((child) => convertNode(child))
            .filter((child) => child !== null)
        }

        return result
      }

      return convertNode(node)
    },
    getUserInitials(name) {
      // Using the same logic as UserAvatar component
      if (!name) return ''
      try {
        return name
          .split(' ')
          .filter((word) => word)
          .map((n) => n[0])
          .join('')
          .toUpperCase()
      } catch (error) {
        console.error('Error in getUserInitials:', error)
        return ''
      }
    },
    getEmployeeImage(item) {
      // Using the UserAvatar component logic to get the employee image
      if (item.systemUserId?.profileImage) {
        return `${this.baseUrl}${item.systemUserId.profileImage}`
      } else {
        if (item.gender === 'M') {
          return require('~/assets/img/profileMale.png')
        } else if (item.gender === 'F') {
          return require('~/assets/img/profileFemale.png')
        } else {
          return require('~/assets/img/profileOther.png')
        }
      }
    },
    getFallbackImage(gender) {
      // Return the fallback image URL based on gender
      if (gender === 'M') {
        return require('~/assets/img/profileMale.png')
      } else if (gender === 'F') {
        return require('~/assets/img/profileFemale.png')
      } else {
        return require('~/assets/img/profileOther.png')
      }
    },
    getInitialsBackgroundColor(initials) {
      // Using the same logic as UserAvatar component
      const colors = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#607D8B']
      if (!initials) return colors[0]
      const index = initials.length % colors.length
      return colors[index]
    },
    getRoleColor(role) {
      const roleColors = {
        CEO: '#94ddff',
        Director: '#ffc7c2',
        Manager: '#e3c2ff',
        'Team Lead': '#d2edc5',
        Developer: '#e9f08f'
      }

      return roleColors[role] || '#ffffff'
    }
  }
}
</script>

<style scoped></style>
